import { useEffect, useState } from "react"
import { ChevronDown } from "lucide-react"

/**
 * A round button that takes you down to the top of the newest reply.
 *
 * Shown only while that reply is below the screen - so it appears when a long
 * thread opens at the top, or when polling brings in an answer while you are
 * reading further up. The reply gets a brief ring on arrival, so it is clear
 * which entry is the newest, then the page goes back to normal.
 */

const FLASH = "animate-flash-ring"
let clearFlash = null

export function JumpToLatest({ targetId }) {
	const [below, setBelow] = useState(false)

	useEffect(() => {
		const target = targetId ? document.getElementById(targetId) : null
		if (!target) {
			setBelow(false)
			return undefined
		}
		const observer = new IntersectionObserver(([entry]) => {
			setBelow(!entry.isIntersecting && entry.boundingClientRect.top > 0)
		})
		observer.observe(target)
		return () => observer.disconnect()
	}, [targetId])

	function jump() {
		const target = document.getElementById(targetId)
		if (!target) return
		// Land on the top of the reply, so a long one is read from its start.
		// The sticky top bar would cover the first lines, so stop below it.
		const header = document.querySelector("header.sticky")
		const offset = (header?.offsetHeight || 0) + 16
		window.scrollTo({
			top: target.getBoundingClientRect().top + window.scrollY - offset,
			behavior: "smooth"
		})

		// Ring it once the scroll has settled, so the flash is seen rather
		// than spent on the way down.
		let done = false
		const flash = () => {
			if (done) return
			done = true
			window.removeEventListener("scrollend", flash)
			target.classList.remove(FLASH)
			void target.offsetWidth // restart the animation on a second click
			target.classList.add(FLASH)
			// Matches the animation's length in tailwind.config.js.
			window.clearTimeout(clearFlash)
			clearFlash = window.setTimeout(() => target.classList.remove(FLASH), 1800)
		}
		window.addEventListener("scrollend", flash)
		window.setTimeout(flash, 900)
	}

	if (!below) return null

	return (
		<button
			type="button"
			onClick={jump}
			className="fixed bottom-6 right-6 z-20 grid h-10 w-10 place-items-center rounded-full border border-ink-200 bg-white text-ink-700 shadow-pop transition hover:border-brand-400 hover:text-brand-700 animate-fade-in"
			aria-label="Jump to the latest reply"
			title="Jump to the latest reply"
		>
			<ChevronDown size={18} aria-hidden />
		</button>
	)
}
